import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useUIStore } from '../store/ui';
import { apiService } from '../services/api';

interface Country {
  code: string;
  name: string;
  flag: string;
  currency: string;
}

interface Region {
  id: number;
  name: string;
}

interface City {
  id: number;
  name: string;
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuthStore();
  const { showToast } = useUIStore();

  const [selectedCountry, setSelectedCountry] = useState(user?.countryCode || 'RU');
  const [selectedRegion, setSelectedRegion] = useState<number | null>(user?.regionId || null);
  const [selectedCity, setSelectedCity] = useState<number | null>(user?.cityId || null);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const countries: Country[] = [
    { code: 'RU', name: 'Россия', flag: '🇷🇺', currency: 'RUB' },
    { code: 'KZ', name: 'Казахстан', flag: '🇰🇿', currency: 'KZT' },
    { code: 'BY', name: 'Беларусь', flag: '🇧🇾', currency: 'BYN' }
  ];

  useEffect(() => {
    if (selectedCountry) {
      loadRegions();
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedRegion) {
      loadCities();
    }
  }, [selectedRegion]);

  const loadRegions = async () => {
    try {
      const response = await apiService.getRegions(selectedCountry);
      setRegions(response.data.data || []);
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  };

  const loadCities = async () => {
    if (!selectedRegion) return;
    try {
      const response = await apiService.getCities(selectedRegion);
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const updates = {
        countryCode: selectedCountry,
        regionId: selectedRegion,
        cityId: selectedCity,
        settings: {
          notifications: notificationsEnabled
        }
      };

      const response = await apiService.updateUser(user.telegramId, updates);
      setUser({ ...user, ...response.data.data });
      
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      showToast('Настройки сохранены!', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Ошибка при сохранении', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    window.Telegram?.WebApp?.showConfirm(
      'Вы уверены, что хотите выйти?',
      (confirmed) => {
        if (confirmed) {
          logout();
          navigate('/');
          window.Telegram?.WebApp?.close();
        }
      }
    );
  };

  const handleDeleteAccount = () => {
    window.Telegram?.WebApp?.showConfirm(
      'Удалить аккаунт? Все данные будут потеряны.',
      async (confirmed) => {
        if (confirmed && user) {
          try {
            await apiService.deleteUser(user.telegramId);
            logout();
            window.Telegram?.WebApp?.close();
          } catch (error) {
            showToast('Ошибка при удалении аккаунта', 'error');
          }
        }
      }
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold">⚙️ Настройки</h1>

      {/* Location Settings */}
      <div className="card space-y-4">
        <h2 className="font-semibold">📍 Местоположение</h2>

        {/* Country */}
        <div>
          <label className="block text-sm font-medium mb-1">Страна</label>
          <div className="grid grid-cols-3 gap-2">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => {
                  setSelectedCountry(country.code);
                  setSelectedRegion(null);
                  setSelectedCity(null);
                }}
                className={`p-3 rounded-lg text-center ${
                  selectedCountry === country.code
                    ? 'bg-tg-theme-button text-tg-theme-button-text'
                    : 'bg-tg-theme-secondary-bg'
                }`}
              >
                <span className="text-xl block">{country.flag}</span>
                <span className="text-xs">{country.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Region */}
        <div>
          <label className="block text-sm font-medium mb-1">Регион</label>
          <select
            className="input"
            value={selectedRegion || ''}
            onChange={(e) => {
              setSelectedRegion(parseInt(e.target.value) || null);
              setSelectedCity(null);
            }}
          >
            <option value="">Выберите регион</option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>

        {/* City */}
        {selectedRegion && cities.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1">Город</label>
            <select
              className="input"
              value={selectedCity || ''}
              onChange={(e) => setSelectedCity(parseInt(e.target.value) || null)}
            >
              <option value="">Выберите город</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Notifications */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">🔔 Уведомления</h2>
            <p className="text-sm text-tg-theme-hint">Получать уведомления о сообщениях</p>
          </div>
          <button
            onClick={() => setNotificationsEnabled(!notificationsEnabled)}
            className={`w-12 h-6 rounded-full transition-colors ${
              notificationsEnabled ? 'bg-tg-theme-button' : 'bg-tg-theme-hint/30'
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full bg-white transition-transform ${
                notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={isLoading}
        className="btn-primary w-full disabled:opacity-50"
      >
        {isLoading ? 'Сохранение...' : '💾 Сохранить изменения'}
      </button>

      {/* Info */}
      <div className="card space-y-3">
        <h2 className="font-semibold">ℹ️ О приложении</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-tg-theme-hint">Версия</span>
            <span>1.0.0</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tg-theme-hint">Telegram ID</span>
            <span>{user?.telegramId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-tg-theme-hint">Зарегистрирован</span>
            <span>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU') : '-'}</span>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="space-y-2">
        <button
          onClick={handleLogout}
          className="btn-secondary w-full"
        >
          🚪 Выйти из аккаунта
        </button>
        <button
          onClick={handleDeleteAccount}
          className="w-full py-3 text-red-500 text-sm hover:bg-red-50 rounded-xl transition-colors"
        >
          🗑 Удалить аккаунт
        </button>
      </div>

      {/* Support */}
      <p className="text-center text-sm text-tg-theme-hint">
        Поддержка: @birga_support
      </p>
    </div>
  );
}
